"use client";

import React from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import dynamic from "next/dynamic";
import { Shield, Zap, Globe, Star, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslations } from "@/lib/i18n-client";
import BackgroundDecor from "@/components/BackgroundDecor";
import TrustpilotBadge from "@/components/shared/TrustpilotBadge";

// Lazy-load DestinationSearch so the heavy pricingData (packages.json ~3.7MB)
// does not land in the initial bundle. A skeleton placeholder preserves layout.
const DestinationSearch = dynamic(() => import("@/components/shared/DestinationSearch"), {
  ssr: false,
  loading: () => (
    <div className="relative">
      <div className="h-11 rounded-lg border border-border/60 bg-background/40 backdrop-blur-sm" />
    </div>
  ),
});

const basePlans = [
  {
    name: "Europe",
    data: "10GB",
    price: "$12",
    validity: "30 Days",
    badge: "Best Seller",
    features: ["4G/5G", "Regional Plan"],
    lookupMode: "regional" as const,
    lookupKey: "EU",
  },
  {
    name: "USA",
    data: "5GB",
    price: "$8",
    validity: "15 Days",
    badge: "Hot Deal",
    features: ["4G/5G", "Local Network"],
    lookupMode: "country" as const,
    lookupKey: "US",
  },
  {
    name: "Asia",
    data: "20GB",
    price: "$25",
    validity: "30 Days",
    badge: "Traveler Choice",
    features: ["Multi-Country", "4G/5G"],
    lookupMode: "regional" as const,
    lookupKey: "SG",
  },
  {
    name: "Global",
    data: "3GB",
    price: "$19",
    validity: "365 Days",
    badge: "Valid 1 Year",
    features: ["140+ Countries", "Keep Backup"],
    lookupMode: "country" as const,
    lookupKey: "GLOBAL",
  },
  {
    name: "Japan",
    data: "Unlimited",
    price: "$15",
    validity: "7 Days",
    badge: "High Speed",
    features: ["Local Network", "4G/5G"],
    lookupMode: "country" as const,
    lookupKey: "JP",
  },
];

export default function Hero() {
  const { t } = useTranslations();

  const planIntentMap: Record<string, { mode: "country" | "regional"; countryCode?: string; region?: string }> = {
    Europe: { mode: "regional", region: "Europe" },
    USA: { mode: "country", countryCode: "US" },
    Asia: { mode: "regional", region: "Asia" },
    Global: { mode: "regional", region: "Global" },
    Japan: { mode: "country", countryCode: "JP" },
  };

  const [plans, setPlans] = React.useState(basePlans);
  const [currentPlanIndex, setCurrentPlanIndex] = React.useState(0);
  const [pauseAutoRotate, setPauseAutoRotate] = React.useState(false);

  // Auto-rotate the hero cards
  React.useEffect(() => {
    // Defer the heavy pricingData import (pulls in packages.json, ~3.7MB raw).
    // Hero ships with sensible hardcoded fallback prices, so refreshing them is
    // a non-critical enhancement. Wait for idle time before loading.
    let cancelled = false;

    const loadDynamicPrices = async () => {
      try {
        const [{ loadPlansAsync, getCountryPlans, getRegionalPlans, retailForPlan }, { evaluatePlanFlags }] = await Promise.all([
          import("@/lib/pricingData"),
          import("@/constants/pricing"),
        ]);
        const allPlans = await loadPlansAsync();
        if (cancelled) return;

        const updatedPlans = basePlans.map((p) => {
          const candidates =
            p.lookupMode === "regional"
              ? getRegionalPlans(allPlans).filter((pl) =>
                pl.Region?.toLowerCase().includes(p.name.toLowerCase()),
              )
              : getCountryPlans(allPlans, p.lookupKey);

          if (!candidates || candidates.length === 0) return p;

          const sorted = [...candidates].sort((a, b) => retailForPlan(a) - retailForPlan(b));
          const usefulPlans = sorted.filter(pl => (pl.GBs || 0) >= 1 || pl.DataType.toLowerCase().includes("unlimited"));
          const pool = usefulPlans.length > 0 ? usefulPlans : sorted;

          const bestValueCandidate = pool.find(pl => {
            const retail = retailForPlan(pl);
            const flags = evaluatePlanFlags(retail, pl.Name, p.lookupKey);
            return flags.isBestValue;
          });

          const selection = bestValueCandidate || pool[0];
          const price = retailForPlan(selection);
          const gb = selection.GBs;

          return {
            ...p,
            price: `$${price.toFixed(0)}`,
            data: selection.DataType === "Daily Unlimited"
              ? "Unlimited"
              : gb > 0 ? `${gb}GB` : p.data,
            validity: `${selection.ValidityDays} Days`,
            badge: bestValueCandidate === selection ? "Best Value" : p.badge
          };
        });
        if (!cancelled) setPlans(updatedPlans);
      } catch {
        // silent — hardcoded basePlans remain visible
      }
    };

    const win = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };

    let idleId: number | undefined;
    let timeoutId: number | undefined;

    if (typeof win.requestIdleCallback === "function") {
      idleId = win.requestIdleCallback(() => { void loadDynamicPrices(); }, { timeout: 3000 });
    } else {
      timeoutId = window.setTimeout(() => { void loadDynamicPrices(); }, 2000);
    }

    return () => {
      cancelled = true;
      if (idleId !== undefined && typeof win.cancelIdleCallback === "function") {
        win.cancelIdleCallback(idleId);
      }
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, []);

  React.useEffect(() => {
    if (pauseAutoRotate) return;
    const timer = window.setInterval(() => {
      setCurrentPlanIndex((prev) => (prev + 1) % plans.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [plans.length, pauseAutoRotate]);

  const getCardStyle = (index: number) => {
    const total = plans.length;
    const diff = (index - currentPlanIndex + total) % total;

    if (diff === 0) {
      return "z-30 scale-100 translate-x-0 opacity-100 shadow-2xl brightness-100";
    }
    if (diff === 1) {
      return "z-10 scale-90 translate-x-6 sm:translate-x-16 -rotate-6 opacity-60 pointer-events-none blur-sm brightness-90";
    }
    if (diff === total - 1) {
      return "z-0 scale-90 -translate-x-6 sm:-translate-x-16 rotate-6 opacity-40 pointer-events-none blur-sm brightness-75";
    }
    return "z-0 scale-75 opacity-0 pointer-events-none";
  };

  const focusPricingForPlan = (planName: string) => {
    try {
      const intent = planIntentMap[planName];
      if (intent) {
        localStorage.setItem("hero_plan_intent", JSON.stringify(intent));
        window.dispatchEvent(new Event("flash_plan_intent"));
      }
    } catch {
      // no-op: localStorage is best effort only
    }

    const plansSection = document.getElementById("pricing");
    if (plansSection) plansSection.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      className="relative min-h-[600px] lg:min-h-[720px] flex items-start"
    >
      <div className="absolute inset-0 bg-gradient-hero pointer-events-none" />
      <BackgroundDecor section="hero" seed={123} />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-28 lg:pt-32 pb-12 lg:pb-20">
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-16 items-center">
          <div className="text-center lg:text-start z-20">
            <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-muted/40 px-3 py-1 text-xs text-foreground mb-5">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-3.5 h-3.5 fill-warning text-warning"
                  />
                ))}
              </div>
              <span>{t("hero.trustedBy", "Connecting travelers worldwide")}</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
              <span className="block">{t("hero.brand", "eSIMSupra")}</span>
              <span className="block">{t("hero.headlineStart", "Stay Connected Everywhere")}</span>
              <span className="block bg-gradient-primary bg-clip-text text-transparent">
                {t("hero.headlineHighlight", "Instant eSIM Activation")}
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-muted-foreground mt-4 max-w-2xl mx-auto lg:mx-0">
              {t(
                "hero.subtitle",
                "Activate in 60 seconds. Plans from $2. QR code delivered by email — no SIM swap, no store visit.",
              )}
            </p>

            <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
              <Button
                variant="hero"
                size="xl"
                onClick={() => {
                  const plansSection = document.getElementById("pricing");
                  if (plansSection)
                    plansSection.scrollIntoView({ behavior: "smooth" });
                }}
              >
                <Clock className="w-5 h-5" />
                {t("hero.ctaPrimary", "Get My eSIM in 60 Seconds")}
              </Button>
              <Button variant="glass" size="xl" asChild>
                <Link href="/#pricing">{t("hero.ctaSecondary", "See Plans & Pricing")}</Link>
              </Button>
            </div>

            <div className="mt-3 text-sm text-muted-foreground">
              {t(
                "hero.trustLine",
                "No roaming surprises. 24/7 human support. Money-back guarantee for incompatible devices.",
              )}
            </div>

            {/* Trustpilot badge */}
            <div className="mt-4">
              <TrustpilotBadge
                className="justify-center lg:justify-start"
              />
            </div>

            {/* Destination search */}
            <div className="mt-5 w-full max-w-md mx-auto lg:mx-0">
              <p className="text-xs text-muted-foreground mb-2">
                {t("hero.searchLabel", "🔍 Where are you traveling?")}
              </p>
              <DestinationSearch />
            </div>

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="glass rounded-xl px-3 py-3 flex items-center justify-center lg:justify-start gap-2">
                <Globe className="w-5 h-5 text-primary" />
                <span className="text-xs sm:text-sm font-medium">
                  {t("hero.statCountries", "180+ Countries")}
                </span>
              </div>
              <div className="glass rounded-xl px-3 py-3 flex items-center justify-center lg:justify-start gap-2">
                <Shield className="w-5 h-5 text-primary" />
                <span className="text-xs sm:text-sm font-medium">
                  {t("hero.statPayments", "Secure Payments")}
                </span>
              </div>
              <div className="glass rounded-xl px-3 py-3 flex items-center justify-center lg:justify-start gap-2">
                <Zap className="w-5 h-5 text-primary" />
                <span className="text-xs sm:text-sm font-medium">
                  {t("hero.statActivation", "Instant Activation")}
                </span>
              </div>
              <div className="glass rounded-xl px-3 py-3 flex items-center justify-center lg:justify-start gap-2">
                <Shield className="w-5 h-5 text-primary" />
                <span className="text-xs sm:text-sm font-medium">
                  {t("hero.statSupport", "24/7 Support")}
                </span>
              </div>
            </div>
          </div>

          <div
            className="relative flex justify-center lg:justify-end h-[500px] lg:h-auto items-center pointer-events-none lg:mt-14"
            onMouseEnter={() => setPauseAutoRotate(true)}
            onMouseLeave={() => setPauseAutoRotate(false)}
          >
            <h2 className="sr-only">{t("hero.plansHeading", "Featured eSIM Plans")}</h2>
            <div className="relative w-full max-w-[320px] sm:max-w-[380px] h-[450px] flex items-center justify-center pointer-events-auto">
              <div className="absolute inset-0 -inset-x-8 blur-3xl bg-gradient-to-br from-primary/25 to-accent/15 rounded-full opacity-55 dark:opacity-40" />

              {plans.map((plan, index) => (
                <div
                  key={plan.name}
                  className={`absolute top-0 left-0 w-full transition-all duration-700 will-change-transform transform-gpu ${getCardStyle(index)}`}
                  style={{ transitionTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)" }}
                >
                  <div
                    className="relative overflow-hidden rounded-[2rem] p-6 sm:p-8 bg-card/92 dark:bg-card/70 border border-border/80 dark:border-white/10 ring-1 ring-black/5 dark:ring-white/5 shadow-[0_22px_55px_-22px_hsl(var(--foreground)/0.45)] dark:shadow-2xl backdrop-blur-2xl cursor-pointer"
                    onClick={() => focusPricingForPlan(plan.name)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        focusPricingForPlan(plan.name);
                      }
                    }}

                  >
                    <div
                      className={`absolute inset-0 bg-gradient-to-tr from-white/10 via-transparent to-transparent pointer-events-none transition-opacity duration-300 ${index === currentPlanIndex ? "opacity-50" : "opacity-20"}`}
                    />

                    <div className="flex justify-between items-start mb-8">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/20 text-primary">
                            <Globe className="w-3.5 h-3.5" />
                          </span>
                          <p className="text-xs font-bold text-primary tracking-wider uppercase">
                            {plan.badge}
                          </p>
                        </div>
                        <h3 className="text-2xl font-bold text-foreground tracking-tight">
                          {plan.name} Plan
                        </h3>
                      </div>

                      <div className="relative group cursor-pointer">
                        <div className="gold-chip w-14 h-11 rounded-lg shadow-lg border border-yellow-600/30 transform group-hover:brightness-110 transition-all">
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-9 h-7 border border-yellow-900/40 rounded-[4px]" />
                          </div>
                          <div className="absolute left-[6px] top-1/2 -translate-y-1/2 flex flex-col gap-[3px]">
                            <div className="w-2.5 h-[1px] bg-yellow-900/40" />
                            <div className="w-2.5 h-[1px] bg-yellow-900/40" />
                            <div className="w-2.5 h-[1px] bg-yellow-900/40" />
                          </div>
                          <div className="absolute right-[6px] top-1/2 -translate-y-1/2 flex flex-col gap-[3px]">
                            <div className="w-2.5 h-[1px] bg-yellow-900/40" />
                            <div className="w-2.5 h-[1px] bg-yellow-900/40" />
                            <div className="w-2.5 h-[1px] bg-yellow-900/40" />
                          </div>
                        </div>
                        <div className="absolute inset-0 rounded-lg glow-gold opacity-40 group-hover:opacity-60 transition-opacity" />
                      </div>
                    </div>

                    <div className="flex items-center gap-8 mb-8 relative">
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1px] h-12 bg-gradient-to-b from-transparent via-border to-transparent" />
                      <div>
                        <div className="text-4xl font-extrabold text-foreground tracking-tight">
                          {plan.data.replace("GB", "")}
                          <span className="text-lg text-muted-foreground ml-1 font-medium">
                            {plan.data.includes("GB") ? "GB" : ""}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground font-medium mt-1">
                          {t('planCard.highSpeedData', 'High-Speed Data')}
                        </div>
                      </div>
                      <div>
                        <div className="text-4xl font-extrabold gradient-text-hero">
                          {plan.price}
                        </div>
                        <p className="text-xs text-muted-foreground font-medium mt-1">
                          {plan.validity} Valid
                        </p>
                      </div>
                    </div>

                    {index === currentPlanIndex && (
                      <>
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 border border-primary/10 rounded-full opacity-30" />
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 border border-dashed border-primary/5 rounded-full opacity-20" />
                      </>
                    )}

                    <div className="flex justify-between items-center pt-2">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/5 border border-white/5">
                          <Zap className="w-3.5 h-3.5 text-warning fill-warning" />
                          <span className="text-[10px] font-semibold text-foreground">
                            {plan.features[0]}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Shield className="w-3.5 h-3.5" />
                          <span className="text-[10px] font-medium">
                            {plan.features[1]}
                          </span>
                        </div>
                      </div>
                      <span className="text-[10px] font-medium text-primary bg-primary/10 px-2 py-1 rounded-full">
                        180+ Countries
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              <>
                <div className="absolute -top-10 -right-10 w-24 h-24 bg-secondary/10 rounded-full opacity-60 animate-pulse" />
                <div className="absolute -bottom-10 -left-5 w-32 h-32 bg-primary/5 rounded-full opacity-60 delay-700 animate-pulse" />
              </>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
